import { ModernFrontendCourse } from './types';

export const MODERN_FRONTEND_COURSES: ModernFrontendCourse[] = [
  {
    id: 'svelte-complete-guide',
    title: 'Svelte/SvelteKit Complete Guide',
    description: 'Master Svelte and SvelteKit for building fast, modern web applications with minimal boilerplate.',
    level: 'intermediate',
    totalDuration: '8 hours',
    image: '/api/placeholder/400/300',
    tags: ['Svelte', 'SvelteKit', 'Frontend', 'Modern JavaScript', 'Performance'],
    prerequisites: ['JavaScript ES6+', 'Basic web development knowledge'],
    estimatedHours: 8,
    objectives: [
      'Build reactive UIs with Svelte',
      'Create full-stack apps with SvelteKit',
      'Master Svelte stores and state management',
      'Implement routing and page transitions'
    ],
    learningOutcomes: [
      'Create reactive components with Svelte syntax',
      'Manage application state with stores',
      'Build full-stack applications with SvelteKit',
      'Implement client and server-side routing',
      'Optimize performance with Svelte compiler',
      'Deploy SvelteKit applications'
    ],
    modules: [
      {
        id: 'svelte-fundamentals',
        title: 'Svelte Fundamentals',
        description: 'Learn Svelte basics and reactive programming',
        content: 'Svelte is a compile-time framework that generates vanilla JavaScript...',
        duration: '2 hours',
        difficulty: 'beginner',
        order: 1,
        estimatedTime: '2 hours',
        learningObjectives: [
          'Understand Svelte\'s compilation model',
          'Create reactive components',
          'Handle user interactions'
        ],
        codeExamples: [
          {
            title: 'Reactive Counter',
            code: '<script>\n  let count = 0;\n  $: doubled = count * 2;\n</script>\n\n<button on:click={() => count++}>\n  Count: {count}\n</button>\n<p>Doubled: {doubled}</p>',
            explanation: 'A reactive counter component showing Svelte\'s reactive statements.',
            language: 'svelte'
          }
        ]
      },
      {
        id: 'svelte-components',
        title: 'Component Communication and Slots',
        description: 'Master component patterns in Svelte',
        content: 'Learn advanced component patterns including props, events, and slots...',
        duration: '2 hours',
        difficulty: 'intermediate',
        order: 2
      },
      {
        id: 'svelte-stores',
        title: 'State Management with Stores',
        description: 'Manage global state with Svelte stores',
        content: 'Svelte stores provide a simple yet powerful state management solution...',
        duration: '1.5 hours',
        difficulty: 'intermediate',
        order: 3
      },
      {
        id: 'sveltekit-basics',
        title: 'SvelteKit Fundamentals',
        description: 'Build full-stack apps with SvelteKit',
        content: 'SvelteKit provides the missing pieces for building production applications...',
        duration: '2 hours',
        difficulty: 'intermediate',
        order: 4
      },
      {
        id: 'sveltekit-advanced',
        title: 'Advanced SvelteKit Features',
        description: 'Master SSR, SSG, and API routes',
        content: 'Explore server-side rendering, static generation, and API development...',
        duration: '0.5 hours',
        difficulty: 'advanced',
        order: 5
      }
    ],
    type: 'modern-frontend'
  },
  {
    id: 'htmx-progressive-enhancement',
    title: 'HTMX for Progressive Enhancement',
    description: 'Build modern web experiences with minimal JavaScript using HTMX for progressive enhancement.',
    level: 'beginner',
    totalDuration: '4 hours',
    image: '/api/placeholder/400/300',
    tags: ['HTMX', 'Progressive Enhancement', 'HTML', 'Hypermedia', 'Web Standards'],
    prerequisites: ['HTML/CSS knowledge', 'Basic understanding of HTTP'],
    estimatedHours: 4,
    objectives: [
      'Implement AJAX functionality with HTML attributes',
      'Create dynamic UIs without JavaScript frameworks',
      'Build accessible progressive web experiences',
      'Integrate HTMX with backend frameworks'
    ],
    learningOutcomes: [
      'Add interactivity with HTMX attributes',
      'Handle form submissions and validation',
      'Implement real-time updates with SSE',
      'Create accessible dynamic interfaces',
      'Optimize for progressive enhancement',
      'Integrate with various backend technologies'
    ],
    modules: [
      {
        id: 'htmx-introduction',
        title: 'Introduction to HTMX',
        description: 'Learn the philosophy and basics of HTMX',
        content: 'HTMX extends HTML with powerful attributes for modern web development...',
        duration: '1 hour',
        difficulty: 'beginner',
        order: 1
      },
      {
        id: 'htmx-interactions',
        title: 'User Interactions and Events',
        description: 'Handle clicks, forms, and user events',
        content: 'Learn to create interactive interfaces with HTMX event handling...',
        duration: '1 hour',
        difficulty: 'beginner',
        order: 2
      },
      {
        id: 'htmx-dynamic-content',
        title: 'Dynamic Content Loading',
        description: 'Load and update content dynamically',
        content: 'Master content loading, swapping, and updating patterns...',
        duration: '1 hour',
        difficulty: 'intermediate',
        order: 3
      },
      {
        id: 'htmx-integration',
        title: 'Backend Integration Patterns',
        description: 'Integrate HTMX with different backends',
        content: 'Learn integration patterns for various server technologies...',
        duration: '1 hour',
        difficulty: 'intermediate',
        order: 4
      }
    ],
    type: 'modern-frontend'
  },
  {
    id: 'solidjs-reactive-development',
    title: 'Solid.js Reactive Development',
    description: 'Build high-performance reactive applications with Solid.js fine-grained reactivity system.',
    level: 'intermediate',
    totalDuration: '6 hours',
    image: '/api/placeholder/400/300',
    tags: ['Solid.js', 'Reactive Programming', 'Performance', 'JSX', 'Frontend'],
    prerequisites: ['React knowledge helpful', 'JavaScript ES6+'],
    estimatedHours: 6,
    objectives: [
      'Understand fine-grained reactivity',
      'Build components with Solid.js',
      'Manage state with signals and stores',
      'Optimize rendering performance'
    ],
    learningOutcomes: [
      'Create reactive UIs with fine-grained updates',
      'Use signals for optimal performance',
      'Build complex applications with SolidJS',
      'Implement routing with Solid Router',
      'Optimize bundle size and runtime performance',
      'Migrate from React patterns to Solid'
    ],
    modules: [
      {
        id: 'solidjs-fundamentals',
        title: 'Solid.js Fundamentals',
        description: 'Learn Solid\'s reactive primitives',
        content: 'Solid.js provides fine-grained reactivity with signals and effects...',
        duration: '2 hours',
        difficulty: 'intermediate',
        order: 1
      },
      {
        id: 'solidjs-components',
        title: 'Components and JSX',
        description: 'Build components with Solid JSX',
        content: 'Learn the differences between Solid and React JSX patterns...',
        duration: '1.5 hours',
        difficulty: 'intermediate',
        order: 2
      },
      {
        id: 'solidjs-state',
        title: 'State Management and Stores',
        description: 'Manage complex state with Solid stores',
        content: 'Explore Solid\'s approach to state management and data flow...',
        duration: '1.5 hours',
        difficulty: 'intermediate',
        order: 3
      },
      {
        id: 'solidjs-routing',
        title: 'Routing and Navigation',
        description: 'Implement client-side routing',
        content: 'Build single-page applications with Solid Router...',
        duration: '1 hour',
        difficulty: 'intermediate',
        order: 4
      }
    ],
    type: 'modern-frontend'
  }
];